import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  completeOnboarding,
  logOnboardingStep,
  setTargetHours as apiSetTargetHours,
  updateProfile,
  useCurrentUserProfile,
  type UserRow,
} from '@busy/api';
import { useToast } from '@busy/ui';
import { useT } from '../lib/i18n';
import { useUI } from '../store/ui';
import { WaitlistModal } from '../components/WaitlistModal';
import {
  YEARS_OPTIONS,
  yearsLabelKey,
  isCanonicalYears,
  missingRequired,
} from '../lib/profileFields';

// 4-step onboarding flow ported from docs/DEMO_REFERENCE.html:5367-5555.
// Sequence:
//   1. Tell us about yourself — language, first name, years cutting, ZIP, phone
//   2. Goals — pick up to 2 from 6
//   3. Connect booking software — v1 has Fullcap (waitlist) + 3 others (waitlist) + Other
//   4. Lesson delivery — time, timezone, days, push/email/weekly toggles
//
// On finish, calls completeOnboarding which sets users.onboarded_at = now() so
// the gate in App.tsx routes the user to /today on subsequent visits.

const GOAL_OPTIONS: Array<{ id: string; titleKey: string; descKey: string; icon: GoalIconName }> = [
  { id: 'new-clients',     titleKey: 'onb.goal.newclients',     descKey: 'onb.goal.newclientsDesc',    icon: 'trending' },
  { id: 'retention',       titleKey: 'onb.goal.retention',      descKey: 'onb.goal.retentionDesc',     icon: 'repeat' },
  { id: 'pricing',         titleKey: 'onb.goal.pricing',        descKey: 'onb.goal.pricingDesc',       icon: 'dollar' },
  { id: 'time-management', titleKey: 'onb.goal.timeManagement', descKey: 'onb.goal.timeManagementDesc',icon: 'timer' },
  { id: 'marketing',       titleKey: 'onb.goal.marketing',      descKey: 'onb.goal.marketingDesc',     icon: 'megaphone' },
  { id: 'skill',           titleKey: 'onb.goal.skill',          descKey: 'onb.goal.skillDesc',         icon: 'scissors' },
];

const PLATFORMS: Array<{ id: string; nameKey: string; subKey: string; logoText: string; logoBg: string }> = [
  { id: 'fullcap', nameKey: 'onb.platform.fullcap', subKey: 'onb.platform.fullcapSub',
    logoText: 'F', logoBg: 'bg-gold-500 text-navy-900' },
  { id: 'booksy',  nameKey: 'onb.platform.booksy',  subKey: 'onb.votepriority',
    logoText: 'B', logoBg: 'bg-pink-500 text-white' },
  { id: 'square',  nameKey: 'onb.platform.square',  subKey: 'onb.votepriority',
    logoText: 'S', logoBg: 'bg-black text-white' },
  { id: 'trafft',  nameKey: 'onb.platform.trafft',  subKey: 'onb.votepriority',
    logoText: 'T', logoBg: 'bg-indigo-500 text-white' },
];

const DAYS = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'] as const;
const DAY_KEYS: Record<(typeof DAYS)[number], string> = {
  M: 'onb.dow.m', Tu: 'onb.dow.tu', W: 'onb.dow.w', Th: 'onb.dow.th',
  F: 'onb.dow.f', Sa: 'onb.dow.sa', Su: 'onb.dow.su',
};

const TIMEZONES = [
  { id: 'America/New_York',    label: 'EST (New York)' },
  { id: 'America/Chicago',     label: 'CST (Chicago)' },
  { id: 'America/Denver',      label: 'MST (Denver)' },
  { id: 'America/Los_Angeles', label: 'PST (Los Angeles)' },
  { id: 'Pacific/Honolulu',    label: 'HST (Honolulu)' },
];

export function Onboarding() {
  const profile = useCurrentUserProfile();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const language = useUI((s) => s.language);
  const setLanguage = useUI((s) => s.setLanguage);

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [saving, setSaving] = useState(false);
  const [waitlistPlatform, setWaitlistPlatform] = useState<string | null>(null);

  // Step 1
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [years, setYears] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');

  // Step 2 — goals (pick up to 2). Demo defaults to ['new-clients', 'retention'].
  const [goals, setGoals] = useState<string[]>(['new-clients', 'retention']);
  // Step 2 — target hours per week. Drives the dashboard's capacity
  // utilization tile + Hit Target / Locked In achievements. Default 40 is
  // the most common full-time barber load; presets cover 20 (part-time) →
  // 50+ (grinder). Stored as a number; 50+ means anything ≥50 — we keep
  // the literal value the user picked.
  const [targetHours, setTargetHours] = useState<number>(40);
  // Step 2 — target days per week. Pairs with targetHours so daily
  // math stays honest (40h / 5 days = 8h/day, not 5.7h/day). Default
  // 5 matches the most common barber schedule.
  const [targetDays, setTargetDays] = useState<number>(5);

  // Step 4 — lesson delivery prefs
  const [deliveryTime, setDeliveryTime] = useState('07:00');
  const [timezone, setTimezone] = useState('America/Chicago');
  const [deliveryDays, setDeliveryDays] = useState<Set<string>>(
    new Set(['M', 'Tu', 'W', 'Th', 'F', 'Sa']),
  );
  const [dailyEmail, setDailyEmail] = useState(true);
  const [push, setPush] = useState(true);
  const [weekly, setWeekly] = useState(true);

  // Already-onboarded users shouldn't see this — bounce to /today.
  useEffect(() => {
    if (profile.data?.onboarded_at) {
      navigate('/today', { replace: true });
    }
  }, [profile.data?.onboarded_at, navigate]);

  // Hydrate from existing profile (Supabase trigger may have already filled
  // some fields, e.g. email; if the user came back to onboarding mid-flight
  // their saved values reappear).
  useEffect(() => {
    if (!profile.data) return;
    if (profile.data.first_name) setFirstName(profile.data.first_name);
    if (profile.data.last_name) setLastName(profile.data.last_name);
    if (profile.data.years_cutting && isCanonicalYears(profile.data.years_cutting)) {
      setYears(profile.data.years_cutting);
    }
    if (profile.data.zip) setZip(profile.data.zip);
    if (profile.data.phone) setPhone(profile.data.phone);
    if (profile.data.goals && profile.data.goals.length > 0) setGoals(profile.data.goals);
    if (profile.data.target_hours_per_week != null) setTargetHours(profile.data.target_hours_per_week);
    if (profile.data.target_days_per_week != null) setTargetDays(profile.data.target_days_per_week);
    if (profile.data.timezone) setTimezone(profile.data.timezone);
  }, [profile.data]);

  function toggleGoal(id: string) {
    setGoals((cur) => {
      if (cur.includes(id)) return cur.filter((g) => g !== id);
      if (cur.length >= 2) return cur; // hard cap at 2
      return [...cur, id];
    });
  }

  function toggleDay(d: string) {
    setDeliveryDays((cur) => {
      const next = new Set(cur);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });
  }

  // Step 1 → 2: persist the basics so they survive a refresh mid-flow.
  // Gate on the same required-fields check the leaderboard uses, so a
  // user who finishes onboarding is guaranteed to be eligible for the
  // leaderboard (no separate "go back and fill in your zip" prompt later).
  async function handleStep1Continue() {
    const missing = missingRequired({
      first_name: firstName.trim() || null,
      zip: zip.trim() || null,
      years_cutting: years || null,
    });
    if (missing.length > 0) {
      const fieldNames = missing.map((f) => t(`profile.field.${f}`)).join(', ');
      toast(t('profile.missingRequired', { fields: fieldNames }), 'error');
      return;
    }
    setSaving(true);
    const { error } = await updateProfile({
      first_name: firstName.trim(),
      last_name: lastName.trim() || null,
      years_cutting: years,
      zip: zip.trim(),
      phone: phone.trim() || null,
    });
    setSaving(false);
    if (error) {
      toast(t('onb.saveError', { message: error.message }), 'error');
      return;
    }
    // Funnel event — step 1 (profile basics) done.
    void logOnboardingStep('step1_profile');
    setStep(2);
  }

  async function handleStep2Continue() {
    setSaving(true);
    // Save goals first (regular profile field), then call setTargetHours
    // which writes both the cache + a row in user_target_hours so the
    // capacity calc picks the right target for any future week edits.
    const goalsRes = await updateProfile({ goals });
    if (goalsRes.error) {
      setSaving(false);
      toast(t('onb.saveError', { message: goalsRes.error.message }), 'error');
      return;
    }
    const targetRes = await apiSetTargetHours(targetHours, targetDays);
    setSaving(false);
    if (targetRes.error) {
      toast(t('onb.saveError', { message: targetRes.error.message }), 'error');
      return;
    }
    // Funnel event — step 2 (goals + target hours) done.
    void logOnboardingStep('step2_goals_target');
    setStep(3);
  }

  async function handleFinish() {
    setSaving(true);
    const prefs: Record<string, unknown> = {
      time: deliveryTime,
      days: Array.from(deliveryDays),
      dailyEmail,
      push,
      weekly,
    };
    const patch: Partial<UserRow> = {
      timezone,
      daily_lesson_time: deliveryTime + ':00',
      delivery_days: Array.from(deliveryDays),
      notification_prefs: prefs,
    };
    const result = await completeOnboarding(patch);
    setSaving(false);
    if (result.error) {
      toast(t('onb.saveError', { message: result.error.message }), 'error');
      return;
    }
    // Funnel event — step 4 (final / notification prefs) done.
    // Step 3 doesn't have its own server-side commit (just local state),
    // so we don't log a row for it; the funnel measures (1 → 2 → 4)
    // which is the actual data we have signal for.
    void logOnboardingStep('step4_complete');
    navigate('/today', { replace: true });
  }

  return (
    <div className="min-h-[100dvh] bg-bg-alt grid place-items-center p-6">
      <div className="w-full max-w-[660px] bg-surface rounded-3xl shadow-md border border-line overflow-hidden">
        {/* Header — navy strip with brand + step label + progress bar */}
        <div className="bg-navy-900 text-white px-6 md:px-8 py-5 flex items-center gap-4">
          <span className="w-10 h-10 rounded-[10px] grid place-items-center overflow-hidden shrink-0">
            <img src="/assets/busy-icon.png" alt="" className="w-full h-full object-cover" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] tracking-[0.14em] uppercase font-bold text-white/60">
              {t('onb.steplabel', { n: step })}
            </div>
            <div className="font-bold text-sm truncate">{t(`onb.step${step}.title`)}</div>
          </div>
          <div className="flex gap-1.5 max-w-[200px] flex-1 hidden sm:flex">
            {[1, 2, 3, 4].map((s) => (
              <span
                key={s}
                className={`flex-1 h-1.5 rounded-full ${
                  s < step
                    ? 'bg-gold-400'
                    : s === step
                    ? 'bg-red-600'
                    : 'bg-white/15'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-7 md:p-9">
          <h2 className="font-display text-3xl md:text-4xl text-navy-900 dark:text-gold-500 mb-2 leading-tight">
            {t(`onb.step${step}.title`)}
          </h2>
          <p className="text-ink-500 text-[15px] leading-relaxed mb-7">
            {t(`onb.step${step}.lede`)}
          </p>

          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-ink-500 mb-2">
                  {t('onb.languagelabel')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <LangPick active={language === 'en'} onClick={() => setLanguage('en')}>
                    English
                  </LangPick>
                  <LangPick active={language === 'es'} onClick={() => setLanguage('es')}>
                    Español
                  </LangPick>
                </div>
                <p className="text-[11px] text-ink-500 mt-1.5 leading-snug">
                  {t('onb.languagehelp')}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label={t('onb.firstname')}
                  placeholder={t('onb.firstnameph')}
                  value={firstName}
                  onChange={setFirstName}
                  help={t('onb.firstnamehelp')}
                  required
                />
                <Field
                  label={t('onb.lastname')}
                  placeholder={t('onb.lastnameph')}
                  value={lastName}
                  onChange={setLastName}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <FieldLabel text={t('onb.years')} required />
                  <select
                    value={years}
                    onChange={(e) => setYears(e.target.value)}
                    className="w-full h-11 px-3 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
                  >
                    <option value="">{t('profile.years.placeholder')}</option>
                    {YEARS_OPTIONS.map((value) => (
                      <option key={value} value={value}>
                        {t(yearsLabelKey(value))}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-ink-500 mt-1.5 leading-snug">{t('onb.yearshelp')}</p>
                </div>
                <Field
                  label={t('onb.zip')}
                  placeholder={t('onb.zipph')}
                  value={zip}
                  onChange={(v) => setZip(v.replace(/\D/g, '').slice(0, 5))}
                  inputMode="numeric"
                  help={t('onb.ziphelp')}
                  required
                />
              </div>

              <Field
                label={t('onb.barberphone')}
                placeholder={t('onb.barberphoneph')}
                value={phone}
                onChange={setPhone}
                type="tel"
                help={t('onb.barberphonehelp')}
              />
            </div>
          )}

          {step === 2 && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                {GOAL_OPTIONS.map((g) => {
                  const selected = goals.includes(g.id);
                  const atLimit = goals.length >= 2 && !selected;
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => toggleGoal(g.id)}
                      disabled={atLimit}
                      className={`text-left rounded-2xl border-[1.5px] p-4 flex items-center gap-3 transition-all ${
                        selected
                          ? 'border-navy-900 dark:border-gold-500 bg-navy-900/[0.04] dark:bg-gold-500/10'
                          : 'border-line-strong bg-surface hover:border-navy-700 dark:hover:border-gold-400'
                      } ${atLimit ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      <span
                        className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${
                          selected
                            ? 'bg-navy-900 text-gold-400 dark:bg-gold-500 dark:text-navy-900'
                            : 'bg-bg-alt text-ink-700'
                        }`}
                      >
                        <GoalIcon name={g.icon} className="w-5 h-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-[15px] leading-tight">{t(g.titleKey)}</div>
                        <div className="text-xs text-ink-500 mt-0.5 leading-snug">
                          {t(g.descKey)}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-[13px] text-ink-500 mt-4">
                {goals.length === 1
                  ? t('onb.goal.maxtwo_one')
                  : t('onb.goal.maxtwo_other', { count: goals.length })}
              </p>

              {/* Target weekly hours — drives the dashboard capacity tile
                  and the Hit Target / Locked In achievements. Presets keep
                  the choice fast; the custom input handles the long tail. */}
              <div className="mt-6 pt-5 border-t border-line">
                <h3 className="font-display text-lg leading-tight">
                  {t('onb.targetHours.heading')}
                </h3>
                <p className="text-[13px] text-ink-500 mt-1 leading-snug">
                  {t('onb.targetHours.lede')}
                </p>
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {[20, 30, 40, 50].map((h) => {
                    const selected =
                      h === 50 ? targetHours >= 50 : targetHours === h;
                    const label = h === 50 ? '50+' : String(h);
                    return (
                      <button
                        key={h}
                        type="button"
                        onClick={() => setTargetHours(h)}
                        className={`h-12 rounded-xl border-[1.5px] font-display text-lg transition-all ${
                          selected
                            ? 'border-navy-900 dark:border-gold-500 bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
                            : 'border-line-strong bg-surface text-ink-900 hover:border-navy-700 dark:hover:border-gold-400'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <label className="text-[12px] text-ink-500" htmlFor="onb-target-custom">
                    {t('onb.targetHours.customLabel')}
                  </label>
                  <input
                    id="onb-target-custom"
                    type="number"
                    min={1}
                    max={100}
                    value={targetHours}
                    onChange={(e) => {
                      const n = Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 0));
                      setTargetHours(n);
                    }}
                    className="w-20 h-9 px-2 rounded-lg border border-line bg-surface text-ink-900 text-center font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
                  />
                  <span className="text-[12px] text-ink-500">
                    {t('onb.targetHours.unit')}
                  </span>
                </div>
              </div>

              {/* Target days per week — pairs with hours to give the
                  daily target an honest denominator. Default 5 (most
                  common barber schedule); 1–7 picker keeps the choice
                  fast on touch. */}
              <div className="mt-5">
                <h3 className="font-display text-base leading-tight">
                  {t('onb.targetDays.heading')}
                </h3>
                <p className="text-[12px] text-ink-500 mt-1 leading-snug">
                  {t('onb.targetDays.lede', {
                    perDay: (targetHours / targetDays).toFixed(1),
                  })}
                </p>
                <div className="grid grid-cols-7 gap-1 mt-2">
                  {[1, 2, 3, 4, 5, 6, 7].map((d) => {
                    const selected = targetDays === d;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setTargetDays(d)}
                        aria-label={String(d)}
                        className={`h-11 rounded-xl border-[1.5px] font-display text-base transition-all ${
                          selected
                            ? 'border-navy-900 dark:border-gold-500 bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
                            : 'border-line-strong bg-surface text-ink-900 hover:border-navy-700 dark:hover:border-gold-400'
                        }`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <div className="space-y-3">
              {PLATFORMS.map((p) => (
                <div
                  key={p.id}
                  className="rounded-2xl border-[1.5px] border-line bg-surface p-4 flex items-center gap-4"
                >
                  <div
                    className={`w-12 h-12 rounded-xl grid place-items-center font-display text-xl shrink-0 ${p.logoBg}`}
                  >
                    {p.logoText}
                  </div>
                  <div className="min-w-0 flex-1">
                    <strong className="block">{t(p.nameKey)}</strong>
                    <span className="text-xs text-ink-500">{t(p.subKey)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setWaitlistPlatform(t(p.nameKey))}
                    className="text-xs font-bold tracking-wide text-navy-900 dark:text-gold-500 hover:underline shrink-0"
                  >
                    {t('onb.notifyMe')}
                  </button>
                </div>
              ))}
              <div className="rounded-2xl border-[1.5px] border-dashed border-line bg-surface p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl grid place-items-center font-display text-xl bg-bg-alt text-ink-500 shrink-0">
                  +
                </div>
                <div className="min-w-0 flex-1">
                  <strong className="block">{t('onb.platform.other')}</strong>
                  <span className="text-xs text-ink-500">{t('onb.platform.otherSub')}</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const name = prompt(t('onb.platform.other') + '?');
                    if (name && name.trim()) setWaitlistPlatform(name.trim());
                  }}
                  className="text-xs font-bold tracking-wide text-navy-900 dark:text-gold-500 hover:underline shrink-0"
                >
                  {t('onb.notifyMe')}
                </button>
              </div>
              <p className="text-[13px] text-ink-500 leading-relaxed pt-3">
                {t('onb.nobookingnote')}
              </p>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-ink-500 mb-2">
                    {t('onb.deliverytime')}
                  </label>
                  <input
                    type="time"
                    value={deliveryTime}
                    onChange={(e) => setDeliveryTime(e.target.value)}
                    className="w-full h-11 px-3 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-ink-500 mb-2">
                    {t('onb.timezone')}
                  </label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full h-11 px-3 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.id} value={tz.id}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-ink-500 mb-2">
                  {t('onb.lessondays')}
                </label>
                <div className="flex gap-1.5 flex-wrap">
                  {DAYS.map((d) => {
                    const selected = deliveryDays.has(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleDay(d)}
                        className={`w-11 h-11 rounded-xl font-bold text-sm transition-colors ${
                          selected
                            ? 'bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
                            : 'bg-bg-alt text-ink-700 border border-line-strong hover:border-navy-700'
                        }`}
                      >
                        {t(DAY_KEYS[d])}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-ink-500 mt-2 leading-snug">{t('onb.daystoggle')}</p>
              </div>

              <ToggleRow
                title={t('onb.dailyEmail')}
                sub={t('onb.dailyEmailSub')}
                checked={dailyEmail}
                onChange={setDailyEmail}
              />
              <ToggleRow
                title={t('onb.push')}
                sub={t('onb.pushSub')}
                checked={push}
                onChange={setPush}
              />
              <ToggleRow
                title={t('onb.weekly')}
                sub={t('onb.weeklySub')}
                checked={weekly}
                onChange={setWeekly}
              />

              <div className="flex items-start gap-3 rounded-2xl border border-gold-300/60 dark:border-gold-500/30 bg-gold-50/60 dark:bg-gold-500/10 p-4">
                <InfoIcon className="w-5 h-5 text-[#93701c] dark:text-gold-300 shrink-0 mt-0.5" />
                <p className="text-sm leading-relaxed text-ink-900">
                  <strong>{t('onb.allset')}</strong> {t('onb.allsetsub')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className="border-t border-line bg-bg-alt px-6 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => (step === 1 ? navigate('/sign-in') : setStep((step - 1) as 1 | 2 | 3))}
            className="h-11 px-4 rounded-lg border border-line bg-surface font-semibold text-ink-700 inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('onb.back')}
          </button>
          <div className="flex items-center gap-2">
            {step === 3 && (
              <button
                type="button"
                onClick={() => setStep(4)}
                className="h-11 px-4 rounded-lg border border-line bg-surface font-semibold text-ink-700"
              >
                {t('onb.connectLater')}
              </button>
            )}
            {step === 1 && (
              <button
                type="button"
                onClick={handleStep1Continue}
                disabled={saving}
                className="h-11 px-5 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {saving ? t('onb.saving') : t('onb.continue')}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {step === 2 && (
              <button
                type="button"
                onClick={handleStep2Continue}
                disabled={saving}
                className="h-11 px-5 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {saving ? t('onb.saving') : t('onb.continue')}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {step === 3 && (
              <button
                type="button"
                onClick={() => setStep(4)}
                className="h-11 px-5 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold inline-flex items-center gap-1.5"
              >
                {t('onb.continue')}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {step === 4 && (
              <button
                type="button"
                onClick={handleFinish}
                disabled={saving}
                className="h-11 px-5 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {saving ? t('onb.saving') : t('onb.finish')}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
      {waitlistPlatform && (
        <WaitlistModal
          platform={waitlistPlatform}
          onClose={() => setWaitlistPlatform(null)}
        />
      )}
    </div>
  );
}

/* ============================ subcomponents ============================ */

function Field({
  label,
  placeholder,
  value,
  onChange,
  help,
  type = 'text',
  inputMode,
  required = false,
}: {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  help?: string;
  type?: string;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
  required?: boolean;
}) {
  return (
    <div>
      <FieldLabel text={label} required={required} />
      <input
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 px-3 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
      />
      {help && <p className="text-[11px] text-ink-500 mt-1.5 leading-snug">{help}</p>}
    </div>
  );
}

// Required gets a red asterisk; optional gets a muted "(optional)" tag. Keeps
// the convention consistent across both screens that use Field.
function FieldLabel({ text, required = false }: { text: string; required?: boolean }) {
  const t = useT();
  return (
    <label className="block text-xs font-bold text-ink-500 mb-2">
      {text}
      {required ? (
        <span className="text-red-600 ml-0.5" aria-hidden>
          *
        </span>
      ) : (
        <span className="text-ink-500 font-medium opacity-60 ml-1">
          ({t('profile.optional').toLowerCase()})
        </span>
      )}
    </label>
  );
}

function LangPick({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-11 rounded-xl font-bold text-sm transition-colors ${
        active
          ? 'bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
          : 'bg-bg-alt text-ink-700 border border-line-strong hover:border-navy-700'
      }`}
    >
      {children}
    </button>
  );
}

function ToggleRow({
  title,
  sub,
  checked,
  onChange,
}: {
  title: string;
  sub: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-surface px-4 py-3 cursor-pointer">
      <div className="min-w-0 flex-1">
        <strong className="block text-sm">{title}</strong>
        <span className="text-xs text-ink-500">{sub}</span>
      </div>
      <span
        className={`relative inline-block w-10 h-6 rounded-full transition-colors shrink-0 ${
          checked ? 'bg-navy-900 dark:bg-gold-500' : 'bg-line-strong'
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="absolute opacity-0 w-full h-full cursor-pointer"
        />
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-4' : ''
          }`}
        />
      </span>
    </label>
  );
}

/* ============================ icons ============================ */

type GoalIconName = 'trending' | 'repeat' | 'dollar' | 'timer' | 'megaphone' | 'scissors';

function GoalIcon({ name, className }: { name: GoalIconName; className?: string }) {
  const props = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (name) {
    case 'trending':
      return (
        <svg {...props}>
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
      );
    case 'repeat':
      return (
        <svg {...props}>
          <polyline points="17 1 21 5 17 9" />
          <path d="M3 11V9a4 4 0 0 1 4-4h14" />
          <polyline points="7 23 3 19 7 15" />
          <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
      );
    case 'dollar':
      return (
        <svg {...props}>
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      );
    case 'timer':
      return (
        <svg {...props}>
          <line x1="10" y1="2" x2="14" y2="2" />
          <line x1="12" y1="14" x2="15" y2="11" />
          <circle cx="12" cy="14" r="8" />
        </svg>
      );
    case 'megaphone':
      return (
        <svg {...props}>
          <path d="M3 11l18-5v12L3 14v-3z" />
          <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
        </svg>
      );
    case 'scissors':
      return (
        <svg {...props}>
          <circle cx="6" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <line x1="20" y1="4" x2="8.12" y2="15.88" />
          <line x1="14.47" y1="14.48" x2="20" y2="20" />
          <line x1="8.12" y1="8.12" x2="12" y2="12" />
        </svg>
      );
  }
}

function ArrowLeft({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function ArrowRight({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}
